import { useQuery } from '@tanstack/react-query'
import { Phone, UsersRound } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { useSearchParams } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Avatar } from '@/components/ui/Avatar'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { SectionRoles } from '@/features/portal/SectionRoles'
import { rosterQuery, staffPortalQuery, type RosterEntry } from '@/features/portal/api'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { humanize } from '@/utils/format'

export function StaffClassesPage() {
  const { t } = useTranslation('portal')
  const withGuardians = useGuardians()
  useDocumentTitle('My classes')
  const columns = useMemo<Column<RosterEntry>[]>(
    () =>
      (
        [
          {
            key: 'roll',
            header: t('Roll'),
            cell: (r) => <span className="tabular">{r.rollNumber ?? '—'}</span>,
            className: 'w-16',
          },
          {
            key: 'name',
            header: t('Student'),
            cell: (r) => (
              <div className="flex items-center gap-3">
                <Avatar name={r.fullName} src={r.photoUrl} size="sm" />
                <div>
                  <p className="font-medium text-ink">{r.fullName}</p>
                  <p className="text-xs text-muted">{r.studentCode}</p>
                </div>
              </div>
            ),
          },
          { key: 'gender', header: t('Gender'), hideBelow: 'md', cell: (r) => humanize(r.gender) },
          {
            key: 'guardian',
            header: t('Guardian'),
            cell: (r) =>
              r.guardian ? (
                <div>
                  <p>{r.guardian.name}</p>
                  {r.guardian.phone ? (
                    <a
                      href={`tel:${r.guardian.phone}`}
                      className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline"
                    >
                      <Phone className="size-3" aria-hidden="true" /> {r.guardian.phone}
                    </a>
                  ) : null}
                </div>
              ) : (
                '—'
              ),
          },
        ] satisfies Column<RosterEntry>[]
      ).filter((c) => withGuardians || c.key !== 'guardian'),
    [t, withGuardians],
  )
  const [params, setParams] = useSearchParams()
  const me = useQuery(staffPortalQuery())
  const sectionId = Number(params.get('section') ?? me.data?.sections[0]?.id ?? 0)
  const roster = useQuery({ ...rosterQuery(sectionId), enabled: sectionId > 0 })

  if (me.isPending) return <LoadingState />
  if (me.error) return <ErrorState onRetry={() => void me.refetch()} />
  const section = me.data.sections.find((s) => s.id === sectionId)

  return (
    <>
      <PageHeader
        title={t('My classes')}
        description={t('Rosters of the sections you teach, are class teacher of or cover today.')}
        breadcrumbs={[{ label: t('My classes') }]}
        homeHref="/staff"
      />
      {me.data.sections.length === 0 ? (
        <Card>
          <EmptyState
            icon={<UsersRound />}
            title={t('No sections assigned')}
            description={t('You have no class or lessons assigned this year.')}
          />
        </Card>
      ) : (
        <Card>
          <CardHeader
            title={section?.name ?? t('Section')}
            description={
              section ? (
                <span className="mt-1 flex flex-col gap-1.5">
                  {t('{{count}} students', { count: section.studentCount })}
                  <SectionRoles section={section} />
                </span>
              ) : undefined
            }
            actions={
              me.data.sections.length > 1 ? (
                <Select
                  aria-label={t('Choose section')}
                  size="sm"
                  value={sectionId}
                  onChange={(e) => setParams({ section: e.target.value })}
                  options={me.data.sections.map((s) => ({ value: s.id, label: s.name }))}
                />
              ) : null
            }
          />
          <DataTable
            caption={t('Roster of {{name}}', { name: section?.name ?? t('section') })}
            columns={columns}
            rows={roster.data}
            getRowId={(r) => r.studentId}
            loading={roster.isPending}
            error={roster.error}
            onRetry={() => void roster.refetch()}
            mobileCard={(r) => (
              <div className="flex items-center gap-3">
                <Avatar name={r.fullName} src={r.photoUrl} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-ink">{r.fullName}</p>
                  <p className="text-xs text-muted">
                    {t('Roll')} {r.rollNumber ?? '—'} · {r.guardian?.phone ?? r.studentCode}
                  </p>
                </div>
              </div>
            )}
          />
        </Card>
      )}
    </>
  )
}
